import type { Exercise } from '../../types.ts';
export const batch5Math: Exercise[] = [
  {
    id: 'math-first-success-position',
    category: 'math',
    title: 'A shuffled queue',
    topic: 'A shuffled queue',
    difficulty: 'Challenging',
    questions: [
      {
        id: 'b5-first-success-position',
        prompt:
          'A uniformly shuffled queue contains 3 priority jobs and 7 ordinary jobs, all distinct. Jobs are processed from the front. What is the expected position, counting the front as position 1, of the first priority job?',
        answer: {
          kind: 'number',
          value: 2.75,
          unit: 'position',
          tolerance: 0.0001,
        },
        explanation:
          'The expected position is 2.75. If every job were priority, the same formula would give 1; if only one were priority, it would give the midpoint of the queue.',
        sectionIds: ['concept', 'application'],
        evidence: 'Independently checked in tests/batch5.test.ts.',
      },
    ],
    reading: {
      title: 'A shuffled queue — concepts and method',
      authorship: 'Original teaching and questions prepared with AI assistance.',
      sources: [],
      sections: [
        {
          id: 'concept',
          title: 'Build the method',
          role: 'Concept',
          blocks: [
            {
              kind: 'paragraph',
              text: 'An expected position is a probability-weighted average over many shuffled queues. It need not be a position that occurs in one particular queue. The first priority job appears after all ordinary jobs that precede it. Thus its position equals one plus the size of the initial ordinary-job gap.',
            },
            {
              kind: 'paragraph',
              text: 'Place the priority jobs in their order of appearance. The ordinary jobs occupy gaps before the first, between successive priorities, and after the last. Let n be the total number of jobs and r the number of priority jobs, so n−r jobs are ordinary. With r priority jobs there are r+1 gaps. Uniform shuffling makes these gap positions symmetric: none is favored for receiving ordinary jobs. Their lengths are dependent, since their sum is fixed, but symmetry still gives each the same expectation.',
            },
            {
              kind: 'equation',
              label: 'Expected initial gap',
              lines: [
                {
                  left: 'ordinary jobs / number of gaps',
                  right: '(n−r)/(r+1)',
                  reason:
                    'The r+1 expected gap lengths sum to the fixed number n−r of ordinary jobs.',
                },
              ],
            },
            {
              kind: 'example',
              title: 'One marked card in a four-card deck',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'The marked card is equally likely to be in positions 1, 2, 3 and 4, so its expected position is 2.5. There are three unmarked cards split between the before and after gaps. Each gap has mean length 1.5, and adding one gives 2.5.',
                },
                {
                  kind: 'table',
                  caption: 'Gap check',
                  headers: ['Marked position', 'Cards before', 'Cards after'],
                  rows: [
                    ['1', '0', '3'],
                    ['2', '1', '2'],
                    ['3', '2', '1'],
                    ['4', '3', '0'],
                  ],
                },
                {
                  kind: 'paragraph',
                  text: 'The gaps are not independent: a larger initial gap forces a smaller final gap. Independence was never needed to average their sum.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'The symmetry can be made explicit. Choose the r priority positions from n; each choice is equally likely. It corresponds uniquely to a list of r+1 nonnegative gap lengths adding to n−r. Permuting those gaps gives another equally likely placement. Consequently their average lengths agree. This is a statement about a uniform shuffle, not about real queues that favor certain arrival times.',
            },
          ],
        },
        {
          id: 'application',
          title: 'Apply and check',
          role: 'Daily application',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Three priorities create four gaps sharing seven ordinary jobs. Each gap has expected length 7/4. The first priority follows the initial gap, so its expected position is 1+7/4 = 11/4.',
            },
            {
              kind: 'equation',
              label: 'Expected first position',
              lines: [
                {
                  left: '1 + 7/4',
                  right: '2.75',
                  reason: 'Count the priority job itself as one additional position.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'A second route uses tail probabilities. For a positive integer position X, its value counts how many thresholds 0,1,… it exceeds. Hence E(X)=ΣP(X>k). Here P(X>k) is the chance that the first k positions are ordinary. These probabilities are 1, 7/10, (7/10)(6/9), and so on, ending after seven ordinary jobs. Their sum is also 2.75.',
            },
            {
              kind: 'paragraph',
              text: 'Dividing ten positions by three priorities would give 10/3, but ignores the terminal gap after the last priority. It treats the endpoints differently without justification. The correct gap argument has four gaps, not three.',
            },
            {
              kind: 'paragraph',
              text: 'The expected position is 2.75. If every job were priority, the same formula would give 1; if only one were priority, it would give the midpoint of the queue.',
            },
          ],
        },
      ],
    },
  },
  {
    id: 'math-repair-repeat',
    category: 'math',
    title: 'A repeated inspection',
    topic: 'A repeated inspection',
    difficulty: 'Challenging',
    questions: [
      {
        id: 'b5-repair-repeat',
        prompt:
          'Each independent inspection succeeds with probability 0.6. An attempt consists of one inspection; after a failure you try again under the same conditions. What is the probability that the first success occurs on an even-numbered attempt?',
        answer: {
          kind: 'number',
          value: 0.2857142857142857,
          unit: 'probability',
          tolerance: 0.0001,
        },
        explanation:
          'The probability is 2/7. Higher early success probability favors odd first-success positions, especially the very first attempt.',
        sectionIds: ['concept', 'application'],
        evidence: 'Independently checked in tests/batch5.test.ts.',
      },
    ],
    reading: {
      title: 'A repeated inspection — concepts and method',
      authorship: 'Original teaching and questions prepared with AI assistance.',
      sources: [],
      sections: [
        {
          id: 'concept',
          title: 'Build the method',
          role: 'Concept',
          blocks: [
            {
              kind: 'paragraph',
              text: 'A first-success event specifies both an eventual success and all the failures before it. Independence allows their probabilities to multiply. If success probability is p, failure probability is q=1−p, and first success on attempt k has probability qᵏ⁻¹p. The exponent counts failed attempts, not the successful attempt itself.',
            },
            {
              kind: 'paragraph',
              text: 'An even first-success position could be 2, 4, 6 and so on. Those events are mutually exclusive, so their probabilities add. They form a geometric series: each successive term has the same ratio q² because reaching the next even position requires two more failures.',
            },
            {
              kind: 'equation',
              label: 'Even first-success positions',
              lines: [
                {
                  left: 'P(even)',
                  right: 'qp + q³p + q⁵p + …',
                  reason: 'Each term identifies a distinct first-success attempt.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'To sum a series a+ar+ar²+… with |r|<1, multiply its sum S by r. The difference S−rS leaves just a; all later terms cancel. Thus S=a/(1−r). The infinite tail shrinks to zero under this condition. Here q<1 because a success has positive probability.',
            },
            {
              kind: 'example',
              title: 'Success probability one half',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'The even-position probabilities are 1/4, 1/16, 1/64,… . The first term is 1/4 and the ratio is 1/4, so their sum is (1/4)/(3/4)=1/3. The remaining probability 2/3 belongs to odd positions.',
                },
                {
                  kind: 'table',
                  caption: 'First few terms',
                  headers: ['First success on', 'Probability'],
                  rows: [
                    ['2', '1/4'],
                    ['4', '1/16'],
                    ['6', '1/64'],
                  ],
                },
                {
                  kind: 'paragraph',
                  text: 'Odd and even are not equally likely just because both contain infinitely many integers. The early attempts carry more probability, and attempt one is odd.',
                },
              ],
            },
          ],
        },
        {
          id: 'application',
          title: 'Apply and check',
          role: 'Daily application',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The daily failure probability is q=0.4 and success probability p=0.6. The even-position series begins 0.24, followed by 0.0384, with common ratio 0.16.',
            },
            {
              kind: 'equation',
              label: 'Sum the even cases',
              lines: [
                {
                  left: '0.24 / (1−0.16)',
                  right: '2/7 ≈ 0.285714',
                  reason: 'Divide both numerator and denominator by 0.12.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'The correct arithmetic is 0.24/0.84 = 2/7, approximately 0.285714. A direct state equation gives the same result: starting before an odd attempt, fail once and succeed on the following even attempt with probability 0.24, or fail both and return to the starting parity with probability 0.16. If E is the desired probability, E=0.24+0.16E.',
            },
            {
              kind: 'paragraph',
              text: 'This parity-state check avoids summing an infinite list and independently verifies the series. It also makes the assumptions visible: if inspection outcomes were dependent or success chances changed after repairs, returning after two failures would not necessarily recreate the same situation.',
            },
            {
              kind: 'paragraph',
              text: 'The probability is 2/7. Higher early success probability favors odd first-success positions, especially the very first attempt.',
            },
          ],
        },
      ],
    },
  },
  {
    id: 'math-rectangle-fence',
    category: 'math',
    title: 'Two adjacent pens',
    topic: 'Two adjacent pens',
    difficulty: 'Challenging',
    questions: [
      {
        id: 'b5-rectangle-fence',
        prompt:
          'A farmer has 120 m of fencing for a rectangular enclosure divided into two pens by one straight internal fence parallel to one side. All four outer sides and the divider use the fencing; there are no gates or existing walls. What is the maximum total enclosed area?',
        answer: {
          kind: 'number',
          value: 600,
          unit: 'square meters',
          tolerance: 0.0001,
        },
        explanation:
          'The maximum total area is 600 square meters. The split between the two pens does not alter that total as long as the one divider runs across the full x dimension.',
        sectionIds: ['concept', 'application'],
        evidence: 'Independently checked in tests/batch5.test.ts.',
      },
    ],
    reading: {
      title: 'Two adjacent pens — concepts and method',
      authorship: 'Original teaching and questions prepared with AI assistance.',
      sources: [],
      sections: [
        {
          id: 'concept',
          title: 'Build the method',
          role: 'Concept',
          blocks: [
            {
              kind: 'paragraph',
              text: 'An optimization problem needs two different relationships: the constraint describing what is available and the objective describing what is being maximized. Here fence length is the constrained resource and area is the objective. Confusing them can lead to choosing a square merely because a square is optimal for a different perimeter problem.',
            },
            {
              kind: 'paragraph',
              text: 'Let x be the length of the side parallel to the divider and y the other side. The two outer x sides plus the divider consume 3x of fencing; the other sides consume 2y. If total fence length is F, the constraint is 3x+2y=F. The total rectangular area is xy, irrespective of where along y the divider is placed.',
            },
            {
              kind: 'equation',
              label: 'Remove one variable',
              lines: [
                {
                  left: 'A(x)',
                  right: 'x(F−3x)/2',
                  reason: 'Solve the fence constraint for y and substitute into area.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'Completing the square identifies the largest value without calculus. A downward-opening quadratic can be written as a constant minus a positive multiple of a square. Because a square is nonnegative, the constant is an upper bound, attained when the squared term is zero. This simultaneously proves a bound and locates a feasible maximizing dimension.',
            },
            {
              kind: 'example',
              title: 'A smaller 60 m enclosure',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'With 3x+2y=60, area is 30x−1.5x² = 150−1.5(x−10)². Thus area is at most 150 m², attained at x=10 and y=15.',
                },
                {
                  kind: 'table',
                  caption: 'Nearby choices with the same fencing',
                  headers: ['x (m)', 'y (m)', 'Area (m²)'],
                  rows: [
                    ['8', '18', '144'],
                    ['10', '15', '150'],
                    ['12', '12', '144'],
                  ],
                },
                {
                  kind: 'paragraph',
                  text: 'The optimal outer rectangle is not square because each extra meter of x consumes three meters of fence, while an extra meter of y consumes only two.',
                },
              ],
            },
          ],
        },
        {
          id: 'application',
          title: 'Apply and check',
          role: 'Daily application',
          blocks: [
            {
              kind: 'paragraph',
              text: 'For F=120, y=60−1.5x and A=60x−1.5x². Complete the square by factoring −1.5 from the x terms, then add and subtract 20² inside the parentheses.',
            },
            {
              kind: 'equation',
              label: 'Expose the upper bound',
              lines: [
                {
                  left: 'A',
                  right: '600 − 1.5(x−20)²',
                  reason: 'The squared term can never be negative.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'The bound is attained at x=20 m and y=30 m. Check the resources: three lengths of 20 plus two lengths of 30 use 120 m. Check the objective: 20×30 gives 600 m². Both dimensions are positive, so this is an allowed enclosure rather than an algebraic solution outside the feasible range.',
            },
            {
              kind: 'paragraph',
              text: 'Using the ordinary rectangle perimeter 2x+2y=120 would ignore the internal divider and overstate attainable area. Counting the divider twice would impose an unnecessary extra fence. Drawing the three parallel x segments and two y segments before writing the equation prevents both errors.',
            },
            {
              kind: 'paragraph',
              text: 'The maximum total area is 600 square meters. The split between the two pens does not alter that total as long as the one divider runs across the full x dimension.',
            },
          ],
        },
      ],
    },
  },
  {
    id: 'math-bus-mixture',
    category: 'math',
    title: 'Two service windows',
    topic: 'Two service windows',
    difficulty: 'Challenging',
    questions: [
      {
        id: 'b5-bus-mixture',
        prompt:
          'A help desk receives 40% of its requests during window A and 60% during window B. In A, 80% of requests are resolved immediately; in B, 50% are. A request chosen uniformly from all requests was not resolved immediately. What is the probability it came from window A?',
        answer: {
          kind: 'number',
          value: 0.21052631578947367,
          unit: 'probability',
          tolerance: 0.0001,
        },
        explanation:
          'The probability is 4/19. This is a descriptive calculation under the given rates, not evidence that changing a request’s arrival window would cause its outcome to change.',
        sectionIds: ['concept', 'application'],
        evidence: 'Independently checked in tests/batch5.test.ts.',
      },
    ],
    reading: {
      title: 'Two service windows — concepts and method',
      authorship: 'Original teaching and questions prepared with AI assistance.',
      sources: [],
      sections: [
        {
          id: 'concept',
          title: 'Build the method',
          role: 'Concept',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Conditioning changes the population being compared. The starting percentages describe all requests, but the observation restricts attention to requests that were not resolved immediately. We must first count or weight that restricted population separately for each window, then compare contributions inside it.',
            },
            {
              kind: 'paragraph',
              text: 'For an event H and an observation O, the joint probability P(H and O) equals P(H) multiplied by P(O given H). This is just a two-stage fraction: the share belonging to H times the fraction of that share also satisfying O. Dividing the joint probability by the total probability of O expresses H as a fraction of the observed population.',
            },
            {
              kind: 'equation',
              label: 'Restrict and rescale',
              lines: [
                {
                  left: 'P(H given O)',
                  right: 'P(H and O) / P(O)',
                  reason: 'The denominator includes every way the observation can occur.',
                },
              ],
            },
            {
              kind: 'example',
              title: 'Two equally sized classes',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'Imagine 100 students in each of two classes. In X, 10 miss a lesson; in Y, 30 miss it. Among the 40 absent students, only 10/40=1/4 come from X, even though X had half the original students.',
                },
                {
                  kind: 'table',
                  caption: 'Use the observed population',
                  headers: ['Class', 'All students', 'Absent'],
                  rows: [
                    ['X', '100', '10'],
                    ['Y', '100', '30'],
                    ['Total', '200', '40'],
                  ],
                },
                {
                  kind: 'paragraph',
                  text: 'Neither the original share one half nor X’s absence rate one tenth answers the conditional question. The denominator is all absent students.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'Natural-frequency tables make the same calculation concrete when percentages are convenient. Choose a hypothetical total that turns every percentage into a whole count. The total is a bookkeeping device, not a claim about the actual sample size. Scaling all counts equally leaves the conditional fraction unchanged.',
            },
          ],
        },
        {
          id: 'application',
          title: 'Apply and check',
          role: 'Daily application',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Use 1,000 hypothetical requests. Four hundred arrive in A and six hundred in B. The unresolved fractions are the complements of the supplied resolution rates: 20% in A and 50% in B.',
            },
            {
              kind: 'table',
              caption: 'Partition the requests',
              headers: ['Window', 'All requests', 'Unresolved immediately'],
              rows: [
                ['A', '400', '80'],
                ['B', '600', '300'],
                ['Total', '1000', '380'],
              ],
            },
            {
              kind: 'equation',
              label: 'Among the unresolved requests',
              lines: [
                {
                  left: 'P(A given unresolved)',
                  right: '80/380 = 4/19',
                  reason: 'The denominator is the unresolved population, not all 1,000 requests.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'The observation makes A less likely than its original 40% share because unresolved requests are proportionally less common there. The result, roughly 21.05%, has the right direction. Using 20/(20+50) would ignore unequal request volumes; using 80% would both reverse the conditional direction and use the wrong outcome.',
            },
            {
              kind: 'paragraph',
              text: 'The probability is 4/19. This is a descriptive calculation under the given rates, not evidence that changing a request’s arrival window would cause its outcome to change.',
            },
          ],
        },
      ],
    },
  },
  {
    id: 'math-rational-fill',
    category: 'math',
    title: 'A tank and a leak',
    topic: 'A tank and a leak',
    difficulty: 'Challenging',
    questions: [
      {
        id: 'b5-rational-fill',
        prompt:
          'An inlet alone fills an empty tank in 6 hours at a constant rate. A drain alone empties a full tank in 10 hours at a constant rate whenever water is present. Starting empty, both are opened for 3 hours; then the drain is closed and the inlet continues. How many hours after the start is the tank first full?',
        answer: {
          kind: 'number',
          value: 7.8,
          unit: 'hours',
          tolerance: 0.0001,
        },
        explanation:
          'The tank first becomes full 7.8 hours after the start. The extra 1.8 hours replace the volume lost through the drain.',
        sectionIds: ['concept', 'application'],
        evidence: 'Independently checked in tests/batch5.test.ts.',
      },
    ],
    reading: {
      title: 'A tank and a leak — concepts and method',
      authorship: 'Original teaching and questions prepared with AI assistance.',
      sources: [],
      sections: [
        {
          id: 'concept',
          title: 'Build the method',
          role: 'Concept',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Completion times are not quantities that can be added or subtracted to combine simultaneous work. Rates are the additive quantities: how much of the tank changes per hour. Choose one full tank as the volume unit. An inlet taking a hours to fill it adds 1/a tank per hour; an outlet taking b hours removes 1/b tank per hour under the specified constant-rate model.',
            },
            {
              kind: 'equation',
              label: 'Net volume change while both operate',
              lines: [
                {
                  left: 'rate',
                  right: 'inlet rate − drain rate',
                  reason: 'The signs reflect opposite effects on the same stored volume.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'When a valve changes state, the governing rate changes too. Split the timeline into intervals with constant rates. In each interval, volume change equals rate times elapsed time. Carry the final volume of the first interval into the second as its starting condition. This is a simple balance equation: stored amount equals starting amount plus additions minus removals.',
            },
            {
              kind: 'example',
              title: 'A different tank',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'Suppose an inlet adds 1/4 tank per hour and a drain removes 1/12. Together they add 1/6 per hour. After two hours the tank is one third full. Closing the drain leaves two thirds to fill at 1/4 per hour, requiring (2/3)/(1/4)=8/3 more hours.',
                },
                {
                  kind: 'table',
                  caption: 'Track the two intervals',
                  headers: ['Interval', 'Net rate', 'Volume added'],
                  rows: [
                    ['First 2 hours', '1/6 tank/hour', '1/3 tank'],
                    ['Next 8/3 hours', '1/4 tank/hour', '2/3 tank'],
                  ],
                },
                {
                  kind: 'paragraph',
                  text: 'The total time is 14/3 hours. Dividing the full tank by the first interval’s rate would wrongly assume the drain stayed open throughout.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'A useful physical check is that the tank stays between empty and full throughout each interval. Real drains often slow as the water level falls, but that behavior is expressly excluded by a constant-rate prompt. The model’s answer should not import a pressure-dependent rate that was never supplied.',
            },
          ],
        },
        {
          id: 'application',
          title: 'Apply and check',
          role: 'Daily application',
          blocks: [
            {
              kind: 'paragraph',
              text: 'While both valves are open, the net rate is 1/6−1/10 = 1/15 tank per hour. Three hours add 3/15=1/5 tank. Since the net rate is positive, starting empty does not create a negative-volume interval.',
            },
            {
              kind: 'equation',
              label: 'Remaining time with inlet alone',
              lines: [
                {
                  left: '(1−1/5) / (1/6)',
                  right: '24/5 = 4.8 hours',
                  reason: 'Divide the remaining four fifths of a tank by the inlet’s rate.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'The total elapsed time is 3+4.8=7.8 hours. Check by a full-volume ledger: the inlet runs for 7.8 hours and contributes 7.8/6=1.3 tanks. The drain runs for three hours and removes 3/10=0.3 tank. Net storage is exactly one tank.',
            },
            {
              kind: 'paragraph',
              text: 'An independent way to see the delay is to compare with the six-hour inlet-only fill. The drain removes 0.3 tank during its three hours, requiring an additional 0.3×6=1.8 hours of inlet operation. That again gives 7.8 hours. This shortcut works because the drain’s duration and rate are fixed independently of the final completion time.',
            },
            {
              kind: 'paragraph',
              text: 'The tank first becomes full 7.8 hours after the start. The extra 1.8 hours replace the volume lost through the drain.',
            },
          ],
        },
      ],
    },
  },
];
